import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  Trash2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Loader2,
  Mail
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';
const PAGE_SIZE = 10;

const AdminContactMessages = ({ filterType = 'all' }) => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  /* FILTER STATES */
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateSort, setDateSort] = useState("latest");
  const [dateRange, setDateRange] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  /* ================= FETCH ================= */
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/contact/admin`);
      const data = await res.json();
      setMessages(data || []);
    } catch (err) {
      console.error("Error loading messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  /* ================= ACTIONS ================= */
  const markRead = async (id) => {
    await fetch(`${API_BASE}/api/contact/admin/read/${id}`, {
      method: "PUT",
    });
    fetchMessages();
  };

  const deleteMsg = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await fetch(`${API_BASE}/api/contact/admin/${id}`, {
      method: "DELETE",
    });
    fetchMessages();
  };

  /* ================= FILTER + SORT ================= */
  const baseFilteredMessages = useMemo(() => {
    let data = [...messages];
    if (filterType === 'corporate') {
      data = data.filter(m => m.subject.includes('Corporate Training'));
    } else if (filterType === 'group') {
      data = data.filter(m => m.subject.includes('Group Training'));
    } else if (filterType === 'legacy') {
      data = data.filter(m => m.subject.includes('Legacy Growth Mastery'));
    } else if (filterType === 'leadership-legacy') {
      data = data.filter(m => m.subject.includes('Leadership Legacy Program'));
    } else if (filterType === 'general') {
      data = data.filter(m => !m.subject.trim().startsWith('[PROPOSAL]'));
    }
    return data;
  }, [messages, filterType]);

  const filteredAndSorted = useMemo(() => {
    let data = [...baseFilteredMessages];
    const now = new Date();

    // Text Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(m =>
        m.name.toLowerCase().includes(term) ||
        m.email.toLowerCase().includes(term) ||
        m.subject.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      data = data.filter((m) =>
        statusFilter === "read" ? m.is_read : !m.is_read
      );
    }

    if (dateRange !== "all") {
      data = data.filter((m) => {
        const created = new Date(m.created_at);
        const diffDays =
          (now - created) / (1000 * 60 * 60 * 24);

        if (dateRange === "today") return diffDays < 1;
        if (dateRange === "7") return diffDays <= 7;
        if (dateRange === "30") return diffDays <= 30;
        return true;
      });
    }

    data.sort((a, b) => {
      const da = new Date(a.created_at);
      const db = new Date(b.created_at);
      return dateSort === "latest" ? db - da : da - db;
    });

    return data;
  }, [baseFilteredMessages, statusFilter, dateSort, dateRange, searchTerm]);

  /* ================= PAGINATION ================= */
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSorted.length / PAGE_SIZE)
  );

  const currentPageSafe = Math.min(page, totalPages);
  const startIndex = (currentPageSafe - 1) * PAGE_SIZE;
  const currentPageItems = filteredAndSorted.slice(
    startIndex,
    startIndex + PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [statusFilter, dateSort, dateRange, searchTerm]);

  /* ================= HELPERS ================= */


  /* ================= UI ================= */
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden min-h-[600px] flex flex-col">
      {/* HEADER PANEL */}
      <div className="p-6 border-b border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-exiser-primary transition-colors"
            title="Back to Dashboard"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">
              {filterType === 'corporate' && 'Corporate Training'}
              {filterType === 'group' && 'Group Training'}
              {filterType === 'legacy' && 'Legacy Growth Mastery Proposals'}
              {filterType === 'leadership-legacy' && 'Leadership Legacy Program Proposals'}
              {filterType === 'general' && 'Contact Messages'}
              {filterType === 'all' && 'All Messages'}
            </h3>
            <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
              {baseFilteredMessages.filter((m) => !m.is_read).length} unread / {baseFilteredMessages.length} total
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={fetchMessages}
            className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-exiser-primary transition-colors text-gray-500"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="p-4 bg-white border-b border-gray-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, email, subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm bg-gray-50"
          />
        </div>

        {/* Status Filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full pl-10 pr-8 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm appearance-none bg-gray-50 cursor-pointer"
          >
            <option value="all">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>

        {/* Date Range */}
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-full pl-10 pr-8 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm appearance-none bg-gray-50 cursor-pointer"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
          </select>
        </div>

        {/* Sort */}
        <div className="relative">
          <select
            value={dateSort}
            onChange={(e) => setDateSort(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm bg-gray-50 cursor-pointer"
          >
            <option value="latest">Latest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </div>

      {/* TABLE AREA */}
      <div className="flex-1 overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider sticky top-0 z-10">
            <tr>
              <th className="px-6 py-4 border-b border-gray-200">Sender Info</th>
              <th className="px-6 py-4 border-b border-gray-200">Subject / Message</th>
              <th className="px-6 py-4 border-b border-gray-200">Date</th>
              <th className="px-6 py-4 border-b border-gray-200">Status</th>
              <th className="px-6 py-4 border-b border-gray-200 text-right">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-20 text-center text-gray-500">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2 text-exiser-primary" />
                  Loading messages...
                </td>
              </tr>
            ) : filteredAndSorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-20 text-center text-gray-500 text-sm">
                  <div className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                      <Mail className="w-6 h-6 text-gray-300" />
                    </div>
                    <p className="font-bold text-gray-900">No messages found</p>
                    <p className="text-gray-400 text-xs">Try adjusting your filters.</p>
                  </div>
                </td>
              </tr>
            ) : (
              currentPageItems.map((m) => (
                <tr key={m.id} className="hover:bg-purple-50/20 transition-colors group align-top">
                  <td className="px-6 py-4 w-64">
                    <div className="font-bold text-gray-900 text-sm">{m.name}</div>
                    <div className="text-xs text-gray-500 font-sans">{m.email}</div>
                    <div className="text-xs text-gray-400 font-sans mt-0.5">{m.phone || "-"}</div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-800">
                      {m.subject}
                    </div>
                    <div className={`text-xs text-gray-500 mt-1 leading-relaxed ${expandedId !== m.id ? 'line-clamp-2' : ''}`}>
                      {m.message}
                    </div>
                    <button
                      onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                      className="text-[10px] font-bold text-exiser-primary mt-2 uppercase tracking-wide hover:underline"
                    >
                      {expandedId === m.id ? "Show Less" : "Read Full Message"}
                    </button>
                  </td>

                  <td className="px-6 py-4 w-32">
                    <div className="text-xs text-gray-500 font-sans">
                      {new Date(m.created_at).toLocaleDateString()}
                    </div>
                    <div className="text-[10px] text-gray-400 font-sans">
                      {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>

                  <td className="px-6 py-4 w-28">
                    {m.is_read ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-500 border border-gray-200">
                        Read
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Unread
                      </span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!m.is_read && (
                        <button
                          onClick={() => markRead(m.id)}
                          className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors border border-green-200"
                          title="Mark as Read"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => deleteMsg(m.id)}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-200"
                        title="Delete Message"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      <div className="p-4 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
        <span className="text-gray-500">
          Page <span className="font-bold text-gray-900">{currentPageSafe}</span> of {totalPages} — Showing {currentPageItems.length} messages
        </span>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPageSafe === 1}
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pNum = i + 1;
              // simple sliding window logic if needed, or keeping it fixed for now like admin bookings
              if (totalPages > 5 && currentPageSafe > 3) {
                pNum = currentPageSafe - 2 + i;
              }
              if (pNum > totalPages) return null;

              return (
                <button
                  key={pNum}
                  onClick={() => setPage(pNum)}
                  className={`w-8 h-8 rounded-lg font-bold transition-colors ${currentPageSafe === pNum
                    ? 'bg-exiser-primary text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  {pNum}
                </button>
              )
            })}
          </div>

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPageSafe === totalPages}
            className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default AdminContactMessages;